/**
 * Talent Cloud — Campaign Opportunities
 * Three-section feed of every campaign touching the TM's roster:
 *   1. Pending Routing — brand brief shortlisted one of their creators, awaiting admin assignment
 *   2. Active — pitched/outreach/locked/live campaigns assigned to their creators
 *   3. Completed — finished campaigns (last 50)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { Sidebar } from '../../components/Sidebar';
import { Briefcase, Clock, Zap, CheckCircle2, IndianRupee, ChevronRight, Instagram, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_META = {
  pitched: { label: 'Pitched', color: '#FFB800' },
  outreach: { label: 'Outreach', color: '#0055FF' },
  locked: { label: 'Locked', color: '#9B6EFF' },
  live: { label: 'Live', color: '#FF451A' },
  completed: { label: 'Completed', color: '#00C972' },
};

const safeDate = (d) => {
  try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ''; }
};

export const TalentOpportunities = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/talent/opportunities`, { withCredentials: true });
        setData(data);
        // Auto-pick the most useful tab on first load: pending (action needed) > active > completed
        if ((data?.pending_routing || []).length > 0) setTab('pending');
        else if ((data?.active || []).length > 0) setTab('active');
        else if ((data?.completed || []).length > 0) setTab('completed');
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]"><div className="text-center py-20"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div></div></div>
    );
  }

  const tabs = [
    { key: 'pending', label: 'Pending Routing', count: data?.pending_routing?.length || 0, icon: Clock, color: '#FFB800' },
    { key: 'active', label: 'Active', count: data?.active?.length || 0, icon: Zap, color: '#FF451A' },
    { key: 'completed', label: 'Completed', count: data?.completed?.length || 0, icon: CheckCircle2, color: '#00C972' },
  ];

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="talent-opportunities-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-2">Talent Cloud</div>
            <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] dark:text-white mb-2">Opportunities.</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Every brand campaign touching your roster — from shortlist to completion.</p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-black/[0.08] dark:border-white/5">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-5 py-3 flex items-center gap-2 text-sm font-medium border-b-2 transition-all ${active ? 'text-[#0F0F0F] dark:text-white' : 'text-[#6B6B6B] dark:text-zinc-500 hover:text-zinc-300 border-transparent'}`}
                  style={{ borderColor: active ? t.color : 'transparent' }}
                  data-testid={`opportunities-tab-${t.key}`}
                >
                  <Icon size={14} style={{ color: t.color }} />
                  {t.label}
                  <span className="font-mono text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: active ? `${t.color}25` : 'rgba(255,255,255,0.05)', color: active ? t.color : '#666' }}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {/* Pending */}
          {tab === 'pending' && (
            <div className="space-y-3">
              {(data?.pending_routing || []).length === 0 ? (
                <EmptyState icon={Clock} message="No pending briefs for your roster." />
              ) : (
                data.pending_routing.map((p, i) => (
                  <motion.div
                    key={p.brand_campaign_id}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="bg-gradient-to-br from-[#FFB800]/10 to-[#0F0F0F] border border-[#FFB800]/30 rounded-2xl p-5"
                    data-testid="opportunity-pending-row"
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 rounded-full bg-[#FFB800]/20 text-[#FFB800] text-[10px] font-mono uppercase tracking-wider">Awaiting Admin Routing</span>
                          <span className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono">{safeDate(p.created_at)}</span>
                        </div>
                        <h3 className="font-display text-xl text-[#0F0F0F] dark:text-white tracking-tight">{p.campaign_name}</h3>
                        <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">from <span className="text-[#0F0F0F] dark:text-white font-medium">{p.brand_name}</span></p>
                      </div>
                      <div className="flex items-center gap-1.5 text-[#0F0F0F] dark:text-white font-mono"><IndianRupee size={14} className="text-[#6B6B6B] dark:text-zinc-500" />{(p.budget || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="bg-black/40 rounded-xl p-3 border border-black/[0.08] dark:border-white/5">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-2">Your roster creators shortlisted</div>
                      <div className="flex flex-wrap gap-2">
                        {p.roster_creators.map((c) => (
                          <Link key={c.id} to={`/talent/creators/${c.id}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-full hover:bg-white/10 transition-colors group" data-testid="pending-roster-creator">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF451A] to-[#9B6EFF] flex items-center justify-center text-[10px] font-bold text-[#0F0F0F] dark:text-white">{c.name?.[0]?.toUpperCase()}</div>
                            <span className="text-sm text-[#0F0F0F] dark:text-white group-hover:text-[#FF451A] transition-colors">{c.name}</span>
                            {c.instagram_handle && <span className="text-xs text-[#6B6B6B] dark:text-zinc-500">@{c.instagram_handle}</span>}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Active */}
          {tab === 'active' && (
            <div className="space-y-3">
              {(data?.active || []).length === 0 ? (
                <EmptyState icon={Zap} message="No active campaigns for your roster right now." />
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-3 flex-wrap text-[11px] font-mono uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/20" data-testid="active-roster-pill">
                      <Zap size={10} /> Roster · {data?.roster_active_count ?? 0}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/20" data-testid="active-execution-pill">
                      <AlertCircle size={10} /> Execution-only · {data?.execution_active_count ?? 0}
                    </span>
                    <span className="text-[#A1A1AA] dark:text-zinc-600 normal-case tracking-normal font-sans">
                      Execution-only means admin routed this campaign to you. The creator stays with their assigned manager — your exclusive roster is unchanged.
                    </span>
                  </div>
                  {data.active.map((a, i) => {
                  const stage = STAGE_META[a.stage] || STAGE_META.pitched;
                  const isExec = a.ownership === 'execution';
                  return (
                    <motion.div key={a.campaign_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} data-testid="opportunity-active-row">
                      <Link
                        to={`/campaigns/${a.campaign_id}/workspace`}
                        className={`block bg-white dark:bg-[#0A0B0D] border rounded-2xl p-5 hover:bg-white/[0.02] transition-colors ${isExec ? 'border-[#FFB800]/20 hover:border-[#FFB800]/40' : 'border-black/[0.08] dark:border-white/5 hover:border-[#FF451A]/40'}`}
                        data-testid={`opportunity-open-workspace-${a.campaign_id}`}
                      >
                        <div className="flex items-start gap-4 flex-wrap">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${stage.color}15`, border: `1px solid ${stage.color}30` }}><Briefcase size={20} style={{ color: stage.color }} /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-display text-lg text-[#0F0F0F] dark:text-white tracking-tight">{a.name}</h3>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider" style={{ backgroundColor: `${stage.color}20`, color: stage.color, border: `1px solid ${stage.color}40` }}>{stage.label}</span>
                              {isExec ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-[#FFB800]/15 text-[#FFB800] border border-[#FFB800]/30" data-testid="ownership-badge-execution" title="Admin routed this campaign to you for execution. The creator is not part of your exclusive roster.">
                                  Execution-only
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-[#FF451A]/15 text-[#FF451A] border border-[#FF451A]/30" data-testid="ownership-badge-roster" title="Creator is on your exclusive roster.">
                                  Roster
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">from <span className="text-[#0F0F0F] dark:text-white font-medium">{a.brand_name}</span> · {a.deliverables}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono">
                              {a.creator?.name && (
                                <span className="inline-flex items-center gap-1">
                                  <Instagram size={10} /> {a.creator.name}
                                </span>
                              )}
                              {a.manager_name && <span>· managed by {a.manager_name}</span>}
                              <span>· {safeDate(a.created_at)}</span>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <div className="text-[#0F0F0F] dark:text-white font-mono flex items-center gap-1 justify-end"><IndianRupee size={13} className="text-[#6B6B6B] dark:text-zinc-500" />{(a.budget || 0).toLocaleString('en-IN')}</div>
                              <div className="text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] dark:text-zinc-600 mt-1">Open workspace →</div>
                            </div>
                            <ChevronRight size={18} className="text-[#6B6B6B] dark:text-zinc-500 hidden md:block" />
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
                </>
              )}
            </div>
          )}

          {/* Completed */}
          {tab === 'completed' && (
            <div className="space-y-2">
              {(data?.completed || []).length === 0 ? (
                <EmptyState icon={CheckCircle2} message="No completed campaigns yet." />
              ) : (
                data.completed.map((c) => (
                  <Link
                    key={c.campaign_id}
                    to={`/campaigns/${c.campaign_id}/workspace`}
                    className="flex items-center gap-3 p-3 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl hover:border-[#00C972]/30 transition-colors"
                    data-testid="opportunity-completed-row"
                  >
                    <CheckCircle2 size={18} className="text-[#00C972] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[#0F0F0F] dark:text-white font-medium truncate">{c.name}</div>
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono uppercase tracking-wider">{c.brand_name} · {c.creator?.name} · {safeDate(c.created_at)}</div>
                    </div>
                    <div className="text-[#0F0F0F] dark:text-white font-mono text-sm flex items-center gap-1"><IndianRupee size={11} className="text-[#6B6B6B] dark:text-zinc-500" />{(c.budget || 0).toLocaleString('en-IN')}</div>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ icon: Icon, message }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border-2 border-dashed border-black/[0.10] dark:border-white/10 rounded-2xl p-12 text-center">
    <Icon size={32} className="mx-auto text-[#A1A1AA] dark:text-zinc-600 mb-3" />
    <p className="text-[#6B6B6B] dark:text-zinc-500">{message}</p>
  </div>
);
